import React, { useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { cn } from '../utils/cn';

const UploadZone = ({ onFileProcess, onUrlProcess }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [url, setUrl] = useState('');

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files.length) processFile(files[0]);
  };

  const handleInput = (e) => {
    const files = e.target.files;
    if (files.length) processFile(files[0]);
  };

  const processFile = (file) => {
    if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
      alert('Please upload a CSV file.');
      return;
    }
    onFileProcess(file);
  };

  const handleUrlSubmit = (e) => {
    e.preventDefault();
    if (!url.trim()) return;
    if (!url.toLowerCase().endsWith('.csv') && !url.includes('csv')) {
      if (!confirm('The URL doesn\'t appear to be a CSV file. Try anyway?')) return;
    }
    onUrlProcess(url.trim());
  };

  return (
    <div className="space-y-6 w-full max-w-2xl mx-auto">
      <div 
        onDragOver={handleDragOver} 
        onDragLeave={handleDragLeave} 
        onDrop={handleDrop} 
        className={cn(
          "relative group cursor-pointer flex flex-col items-center justify-center w-full min-h-[14rem] sm:min-h-[16rem] rounded-2xl sm:rounded-[2.5rem] border-2 border-dashed transition-all duration-300 overflow-hidden",
          isDragging ? "border-blue-500 bg-blue-50/10 scale-[1.01]" : "border-slate-300 dark:border-slate-700 hover:border-blue-400 bg-slate-50 dark:bg-slate-900/50"
        )}
      >
        <input type="file" accept=".csv" className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10" onChange={handleInput} />
        <div className="z-0 flex flex-col items-center text-center p-6 space-y-4">
          <div className={cn(
            "p-4 rounded-full transition-all duration-300 shadow-sm",
            isDragging ? "bg-blue-100 text-blue-600 scale-110" : "bg-white dark:bg-slate-800 text-slate-400 dark:text-slate-500"
          )}>
            <SafeIcon icon={FiIcons.FiUploadCloud} className="w-8 h-8" />
          </div>
          <div className="space-y-1">
            <h3 className="text-lg sm:text-xl font-black text-slate-800 dark:text-slate-100 tracking-tight">
              {isDragging ? 'Drop keyword file' : 'Upload CSV Data'}
            </h3>
            <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 font-medium">
              Drag & drop or click to browse.
            </p>
          </div>
        </div>
      </div>

      <div className="relative">
        <div className="absolute inset-0 flex items-center" aria-hidden="true">
          <div className="w-full border-t border-slate-200 dark:border-slate-800"></div>
        </div>
        <div className="relative flex justify-center text-xs sm:text-[10px] font-black uppercase tracking-[0.2em]">
          <span className="bg-[#fcfcfd] dark:bg-slate-950 px-4 text-slate-400">Or use remote source</span>
        </div>
      </div>

      <form onSubmit={handleUrlSubmit} className="flex gap-2">
        <div className="relative flex-1">
          <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
            <SafeIcon icon={FiIcons.FiLink} className="w-3.5 h-3.5 text-slate-400" />
          </div>
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://example.com/keywords.csv"
            className="block w-full pl-10 pr-4 py-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-bold focus:ring-2 focus:ring-yellow-500/20 focus:border-yellow-500 outline-none transition-all dark:text-white"
          />
        </div>
        <button 
          type="submit"
          className="px-6 py-3 bg-slate-900 dark:bg-white text-white dark:text-slate-900 text-xs font-black uppercase tracking-widest rounded-xl hover:bg-yellow-500 dark:hover:bg-yellow-500 hover:text-white transition-all shadow-lg shadow-slate-200 dark:shadow-none"
        >
          Fetch
        </button>
      </form>

      <div className="flex items-center justify-center gap-3">
        <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
        <span className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-slate-500">Fast Cloud Processing</span>
      </div>
    </div>
  );
};

export default UploadZone;